import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const TRANSITION_DURATION = 400; // 0.4 second, snappier

export default function PageTransition({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [showContent, setShowContent] = useState(true);
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    // Only trigger on path change, not on query params or hash
    if (location.pathname === prevPathRef.current) return;
    prevPathRef.current = location.pathname;

    // Start transition
    setShowContent(false);
    setIsLoading(true);

    const timer = setTimeout(() => {
      setIsLoading(false);
      setShowContent(true);
    }, TRANSITION_DURATION);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <>
      {/* Page Content with fade animation */}
      <div
        className={`transition-opacity duration-500 ${
          showContent ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {children}
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center animate-loader-fade-in">
          {/* Rotating spinner ring */}
          <div className="relative w-16 h-16">
            {/* Background ring */}
            <div className="absolute inset-0 rounded-full border-4 border-gray-100" />
            {/* Animated golden arc */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                border: '4px solid transparent',
                borderTopColor: '#D4A843',
                borderRightColor: '#D4A843',
                animation: 'loaderSpin 0.8s linear infinite',
              }}
            />
          </div>

          {/* Brand name */}
          <p className="mt-6 text-brown-800 text-sm font-medium tracking-wider">
            CJJY
          </p>
          <p className="mt-1 text-gold-500 text-[10px] tracking-widest uppercase">
            JINAN CHINA
          </p>

          {/* Loading dots */}
          <div className="flex items-center gap-1 mt-4">
            <span
              className="w-1.5 h-1.5 rounded-full bg-gold-400"
              style={{ animation: 'loaderBounce 1.2s infinite 0ms' }}
            />
            <span
              className="w-1.5 h-1.5 rounded-full bg-gold-400"
              style={{ animation: 'loaderBounce 1.2s infinite 200ms' }}
            />
            <span
              className="w-1.5 h-1.5 rounded-full bg-gold-400"
              style={{ animation: 'loaderBounce 1.2s infinite 400ms' }}
            />
          </div>
        </div>
      )}
    </>
  );
}